import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { TokenService } from './tokenService';
import { AuthService } from './authService';
import { loadingTracker } from './loadingTracker';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** true = no muestra el loading global (llamadas de fondo, ej. contadores). */
    sinLoading?: boolean;
    /** Uso interno: la petición está contada en loadingTracker. */
    _loading?: 'consulta' | 'bloqueante';
  }
}

/** Lo que modifica datos bloquea la pantalla; GET y las búsquedas (POST /buscar) solo muestran la barra. */
const esBloqueante = (config: InternalAxiosRequestConfig): boolean => {
  const method = (config.method || 'get').toLowerCase();
  return !['get', 'head', 'options'].includes(method) && !/\/buscar(\/|\?|$)/.test(config.url || '');
};

const terminarLoading = (config?: InternalAxiosRequestConfig) => {
  if (config?._loading) {
    loadingTracker.end(config._loading === 'bloqueante');
    // El reintento tras refrescar el token reutiliza este config y se cuenta de nuevo.
    config._loading = undefined;
  }
};

// Create main API instance
const apiClient = axios.create({
  // En producción el frontend se sirve desde el mismo App Service que el backend
  // (jar único), por lo que sin REACT_APP_API_BASE_URL explícito se usan rutas relativas.
  baseURL: process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080'),
  timeout: 15000,
  withCredentials: true, // This ensures cookies are sent with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = TokenService.getToken();

    // Id de correlación para rastrear la petición en los logs del backend.
    // Si la vista ya envió uno (reintentos de una misma operación) se respeta.
    if (config.headers && !config.headers['X-Correlation-Id']) {
      config.headers['X-Correlation-Id'] = newCorrelationId();
    }
    

    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (!config.sinLoading) {
      const bloqueante = esBloqueante(config);
      config._loading = bloqueante ? 'bloqueante' : 'consulta';
      loadingTracker.start(bloqueante);
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle responses and errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    terminarLoading(response.config);
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }
    
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    terminarLoading(originalRequest);

    // Log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[API Response Error]', {
        url: originalRequest?.url,
        status: error.response?.status,
        message: error.message,
        data: error.response?.data,
      });
    }
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const newToken = await AuthService.refreshToken();
        
        if (newToken) {
          processQueue(null, newToken);
          
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          
          return apiClient(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Redirect to login
        AuthService.logout();
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // Handle other HTTP errors
    agregarCodigoDeError(error);
    const errorMessage = getErrorMessage(error);
    
    // Create enhanced error object
    const enhancedError = {
      ...error,
      message: errorMessage,
      isNetworkError: !error.response,
      status: error.response?.status,
      data: error.response?.data,
    };
    
    return Promise.reject(enhancedError);
  }
);

// Cuando el backend registró el error (500 o respuesta con correlationId), se agrega el código al
// mensaje que ven las vistas para que el usuario lo copie y soporte lo busque en los logs.
const agregarCodigoDeError = (error: AxiosError): void => {
  const response = error.response;
  if (!response) return;
  const data = response.data as any;
  const codigo: string | undefined =
    data?.correlationId || (response.headers?.['x-correlation-id'] as string | undefined);
  if (!codigo || (response.status < 500 && !data?.correlationId)) return;
  if (data && typeof data === 'object') {
    const base = data.message || 'Error inesperado del servidor';
    if (!String(base).includes(codigo)) data.message = `${base} (Código: ${codigo})`;
  }
};

// Helper function to extract meaningful error messages
const getErrorMessage = (error: AxiosError): string => {
  if (error.response) {
    // Server responded with error status
    const data = error.response.data as any;
    
    if (typeof data === 'string') {
      return data;
    }
    
    if (data?.message) {
      return data.message;
    }
    
    if (data?.error) {
      return data.error;
    }
    
    // Default messages for common HTTP status codes
    switch (error.response.status) {
      case 400:
        return 'Solicitud inválida. Verifique los datos enviados.';
      case 401:
        return 'No autorizado. Por favor, inicie sesión nuevamente.';
      case 403:
        return 'No tiene permisos para realizar esta acción.';
      case 404:
        return 'Recurso no encontrado.';
      case 409:
        return 'Conflicto. El recurso ya existe o está en uso.';
      case 422:
        return 'Datos de entrada inválidos.';
      case 500:
        return 'Error interno del servidor. Intente nuevamente más tarde.';
      case 502:
        return 'Error de conexión con el servidor.';
      case 503:
        return 'Servicio no disponible temporalmente.';
      default:
        return `Error del servidor (${error.response.status}).`;
    }
  } else if (error.request) {
    // Network error
    return 'Error de conexión. Verifique su conexión a internet.';
  } else {
    // Other error
    return error.message || 'Error inesperado.';
  }
};

// Utility functions for common API patterns
export const apiUtils = {
  // GET request with error handling
  get: async <T = any>(url: string, config?: any): Promise<T> => {
    const response = await apiClient.get<T>(url, config);
    return response.data;
  },
  
  // POST request with error handling
  post: async <T = any>(url: string, data?: any, config?: any): Promise<T> => {
    const response = await apiClient.post<T>(url, data, config);
    return response.data;
  },
  
  // PUT request with error handling
  put: async <T = any>(url: string, data?: any, config?: any): Promise<T> => {
    const response = await apiClient.put<T>(url, data, config);
    return response.data;
  },
  
  // DELETE request with error handling
  delete: async <T = any>(url: string, config?: any): Promise<T> => {
    const response = await apiClient.delete<T>(url, config);
    return response.data;
  },
  
  // Upload file with progress
  upload: async <T = any>(
    url: string, 
    file: File, 
    onProgress?: (progress: number) => void
  ): Promise<T> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    
    return response.data;
  },
};

export default apiClient;
/** Mensaje de negocio devuelto por el backend ({message}) o, si no hay, el del error. */
export const getApiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.message || fallback;

/** Id único por intento de operación; se envía como X-Correlation-Id para que el backend deduplique. */
export const newCorrelationId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

export const correlationHeaders = (correlationId?: string) =>
  correlationId ? { headers: { 'X-Correlation-Id': correlationId } } : undefined;
